import React, { useState, useEffect, useCallback } from 'react';
import { Button, Space, message, Input, Select, Card, Image, Typography, Row, Col, Modal, List } from 'antd';
import { useNavigate } from 'react-router-dom';
import { fetchProducts, fetchProductImages, fetchProductVariants, type ProductDto, type ProductImageDto, type ProductVariantDto } from '../services/productService';
import { SearchOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { api, FILE_BASE_URL } from '../services/api';
import { useCurrency } from '../contexts/CurrencyContext';
import { fetchChannels, type ChannelDto } from '../services/channelService';
import { useAuth } from '../contexts/AuthContext';

const { Text } = Typography;

type Product = ProductDto;

interface Category {
  id: number;
  categoryName: string;
  parentId?: number | null;
}

const ProductGallery: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const roleCodes = user?.roleCodes ?? [];
  const canSeePrice = roleCodes.includes('ADMIN');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>();
  const [selectedChannel, setSelectedChannel] = useState<number | undefined>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [channels, setChannels] = useState<ChannelDto[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [itemsPerRow, setItemsPerRow] = useState<number>(4);
  const [productImages, setProductImages] = useState<Record<number, ProductImageDto | null>>({});
  const [variantsByProduct, setVariantsByProduct] = useState<Record<number, ProductVariantDto[]>>({});
  const { baseCurrencySymbol } = useCurrency();
  
  // Raf lokasyonları popup state
  const [shelfModalVisible, setShelfModalVisible] = useState(false);
  const [selectedShelfLocations, setSelectedShelfLocations] = useState<Array<{ shelfCode: string; quantity: number; warehouseName: string }>>([]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get<Category[]>('/categories');
      setCategories(data);
    } catch (error) {
      console.error('Kategoriler yüklenirken hata oluştu:', error);
    }
  };

  const loadChannels = async () => {
    try {
      const data = await fetchChannels();
      setChannels(data.filter(c => c.isActive));
    } catch (error) {
      console.error('Kanallar yüklenirken hata oluştu:', error);
    }
  };

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading products with filters:', {
        page: pagination.current,
        pageSize: pagination.pageSize,
        categoryId: selectedCategory,
        search: searchText.trim() || undefined,
        channelId: selectedChannel
      });
      const result = await fetchProducts(
        pagination.current,
        pagination.pageSize,
        selectedCategory,
        searchText.trim() || undefined,
        selectedChannel
      );
      console.log('✅ Products loaded:', result.data.length, 'items');
      setProducts(result.data);
      setPagination(prev => ({
        ...prev,
        total: result.totalCount,
      }));
    } catch (error) {
      console.error('❌ Error loading products:', error);
      message.error('Ürünler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedChannel, searchText, pagination.current, pagination.pageSize]);

  useEffect(() => {
    loadCategories();
    loadChannels();
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Load primary images for products and variants
  useEffect(() => {
    const loadProductImages = async () => {
      const imagePromises = products.map(async (product) => {
        if (productImages[product.id] === undefined) {
          try {
            const images = await fetchProductImages(product.id);
            const primaryImage = images.find(img => img.isPrimary) || images[0] || null;
            return { productId: product.id, image: primaryImage };
          } catch (error) {
            return { productId: product.id, image: null };
          }
        }
        return null;
      });

      const results = await Promise.all(imagePromises);
      setProductImages(prev => {
        const newImages = { ...prev };
        results.forEach(result => {
          if (result) {
            newImages[result.productId] = result.image;
          }
        });
        return newImages;
      });
    };

    if (products.length > 0) {
      loadProductImages();
    }
  }, [products]);

  // Load variants for products that have variants
  useEffect(() => {
    const loadVariants = async () => {
      const variantPromises = products
        .filter(p => p.isPriceVariant)
        .map(async (product) => {
          if (!variantsByProduct[product.id]) {
            try {
              const variants = await fetchProductVariants(product.id);
              return { productId: product.id, variants };
            } catch (error) {
              console.error(`Varyantlar yüklenirken hata (ProductId: ${product.id}):`, error);
              return { productId: product.id, variants: [] };
            }
          }
          return null;
        });

      const results = await Promise.all(variantPromises);
      setVariantsByProduct(prev => {
        const newVariants = { ...prev };
        results.forEach(result => {
          if (result) {
            newVariants[result.productId] = result.variants;
          }
        });
        return newVariants;
      });
    };

    if (products.length > 0) {
      loadVariants();
    }
  }, [products]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadProducts();
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  const handleProductCodeClick = (product: Product) => {
    navigate(`/products/edit/${product.id}`);
  };

  // Kategorileri hiyerarşik sırala (parent-child ilişkisine göre)
  const getSortedCategoriesHierarchically = (categories: Category[]): Array<Category & { level: number; fullName: string }> => {
    const result: Array<Category & { level: number; fullName: string }> = [];
    
    // Recursive olarak kategori ve alt kategorilerini ekle
    const addCategoryAndChildren = (parentId: number | null | undefined, level: number = 0) => {
      const children = categories
        .filter(c => c.parentId === parentId)
        .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
      
      children.forEach(cat => {
        // Kategori yolunu oluştur (parent > child formatında)
        let fullName = cat.categoryName;
        let currentCat = cat;
        while (currentCat.parentId) {
          const parent = categories.find(c => c.id === currentCat.parentId);
          if (!parent) break;
          fullName = `${parent.categoryName} > ${fullName}`;
          currentCat = parent;
        }
        
        result.push({
          ...cat,
          level,
          fullName
        });
        
        // Alt kategorileri recursive ekle
        addCategoryAndChildren(cat.id, level + 1);
      });
    };
    
    // Kök kategorilerden başla (parentId null/undefined olanlar)
    addCategoryAndChildren(null, 0);
    addCategoryAndChildren(undefined, 0);
    
    return result;
  };

  const categoriesWithHierarchy = getSortedCategoriesHierarchically(categories);

  // Calculate column span based on itemsPerRow
  const getColSpan = () => {
    return 24 / itemsPerRow;
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Ürün Galerisi</h1>
        <Space>
          <span>Yan yana göster:</span>
          <Select
            value={itemsPerRow}
            onChange={setItemsPerRow}
            style={{ width: 100 }}
          >
            <Select.Option value={2}>2</Select.Option>
            <Select.Option value={4}>4</Select.Option>
          </Select>
        </Space>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Input
            placeholder="Ürün ara..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 300 }}
          />
          <Select
            placeholder="Kategori seçiniz"
            value={selectedCategory}
            onChange={(value) => {
              setSelectedCategory(value);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            style={{ width: 350 }}
            allowClear
            showSearch
            filterOption={(input, option) => {
              const categoryName = categoriesWithHierarchy.find(c => c.id === option?.value)?.categoryName || '';
              return categoryName.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {categoriesWithHierarchy.map(category => (
              <Select.Option key={category.id} value={category.id}>
                <span style={{ paddingLeft: `${category.level * 20}px` }}>
                  {category.level > 0 && '└─ '}
                  {category.categoryName}
                </span>
              </Select.Option>
            ))}
          </Select>
          {channels.length > 0 && (
            <Select
              placeholder="Kanal seçiniz"
              value={selectedChannel}
              onChange={(value) => {
                console.log('🎯 Channel selected:', value);
                setSelectedChannel(value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              style={{ width: 200 }}
              allowClear
            >
              {channels.map(channel => (
                <Select.Option key={channel.id} value={channel.id}>
                  {channel.name}
                </Select.Option>
              ))}
            </Select>
          )}
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            Ara
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        {products.flatMap((product) => {
          const primaryImage = productImages[product.id];
          const imageUrl = primaryImage ? `${FILE_BASE_URL}${primaryImage.thumbnailFilePath}` : null;
          const colSpan = itemsPerRow === 2 ? 12 : 6;

          // Eğer ürünün varyantı varsa, varyantları göster
          if (product.isPriceVariant) {
            const variants = variantsByProduct[product.id] || [];
            
            // Varyantlar yükleniyorsa boş döndür (loading state)
            if (variants.length === 0 && !variantsByProduct.hasOwnProperty(product.id)) {
              return [];
            }

            // Varyantları göster
            return variants.map((variant) => {
              const variantValues = variant.values.map(v => `${v.variantName}: ${v.variantValue}`).join(' | ');
              
              return (
                <Col key={`variant-${variant.id}`} xs={24} sm={12} md={colSpan} lg={colSpan} xl={colSpan}>
                  <Card
                    hoverable
                    loading={loading}
                    style={{ height: '100%' }}
                    bodyStyle={{ padding: '12px' }}
                  >
                    <div style={{ 
                      width: '100%', 
                      height: '200px', 
                      backgroundColor: '#f5f5f5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '12px',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      {imageUrl ? (
                        <Image
                          src={imageUrl}
                          alt={product.productName}
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover' 
                          }}
                          preview={false}
                        />
                      ) : (
                        <div style={{ color: '#999', fontSize: '14px' }}>Görsel Yok</div>
                      )}
                    </div>
                    <div>
                      <Text 
                        strong 
                        style={{ 
                          cursor: 'pointer',
                          color: '#1890ff',
                          fontSize: '14px',
                          display: 'block',
                          marginBottom: '4px'
                        }}
                        onClick={() => handleProductCodeClick(product)}
                      >
                        {variant.sku}
                      </Text>
                      <Text 
                        style={{ 
                          fontSize: '13px',
                          display: 'block',
                          marginBottom: '4px',
                          color: '#666'
                        }}
                      >
                        {product.productName}
                      </Text>
                      {variantValues && (
                        <Text 
                          type="secondary" 
                          style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}
                        >
                          {variantValues}
                        </Text>
                      )}
                      <Text 
                        type="secondary" 
                        style={{ fontSize: '12px', display: 'block' }}
                      >
                        {product.categoryName}
                      </Text>
                      {canSeePrice && variant.price > 0 && (
                        <Text 
                          strong
                          style={{ fontSize: '13px', display: 'block', marginTop: '4px', color: '#1890ff' }}
                        >
                          Perakende (KDV dahil): {baseCurrencySymbol}{(variant.price * (1 + (product.taxRate ?? 20) / 100)).toFixed(2)}
                        </Text>
                      )}
                      {canSeePrice && variant.wholesalePrice && variant.wholesalePrice > 0 && (
                        <Text 
                          type="secondary" 
                          style={{ fontSize: '12px', display: 'block', marginTop: '2px' }}
                        >
                          Toptan (KDV dahil): {baseCurrencySymbol}{(variant.wholesalePrice * (1 + (product.taxRate ?? 20) / 100)).toFixed(2)}
                        </Text>
                      )}
                      {canSeePrice && variant.purchasePrice && variant.purchasePrice > 0 && (
                        <Text 
                          type="secondary" 
                          style={{ fontSize: '12px', display: 'block', marginTop: '2px' }}
                        >
                          Alış (KDV dahil): {baseCurrencySymbol}{(variant.purchasePrice * (1 + (product.taxRate ?? 20) / 100)).toFixed(2)}
                        </Text>
                      )}
                      {variant.stock !== undefined && (
                        <Text 
                          type="secondary" 
                          style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}
                        >
                          Stok: {variant.stock}
                        </Text>
                      )}
                    </div>
                  </Card>
                </Col>
              );
            });
          } else {
            // Varyantı yoksa ana ürünü göster
            return (
              <Col key={product.id} xs={24} sm={12} md={colSpan} lg={colSpan} xl={colSpan}>
                <Card
                  hoverable
                  loading={loading}
                  style={{ height: '100%' }}
                  bodyStyle={{ padding: '12px' }}
                >
                  <div style={{ 
                    width: '100%', 
                    height: '200px', 
                    backgroundColor: '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    {imageUrl ? (
                      <Image
                        src={imageUrl}
                        alt={product.productName}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover' 
                        }}
                        preview={false}
                      />
                    ) : (
                      <div style={{ color: '#999', fontSize: '14px' }}>Görsel Yok</div>
                    )}
                  </div>
                  <div>
                    <Text 
                      strong 
                      style={{ 
                        cursor: 'pointer',
                        color: '#1890ff',
                        fontSize: '14px',
                        display: 'block',
                        marginBottom: '4px'
                      }}
                      onClick={() => handleProductCodeClick(product)}
                    >
                      {product.productCode}
                    </Text>
                    <Text 
                      style={{ 
                        fontSize: '13px',
                        display: 'block',
                        marginBottom: '4px',
                        color: '#666'
                      }}
                    >
                      {product.productName}
                    </Text>
                    <Text 
                      type="secondary" 
                      style={{ fontSize: '12px', display: 'block' }}
                    >
                      {product.categoryName}
                    </Text>
                    {canSeePrice && product.retailPrice && product.retailPrice > 0 && (
                      <Text 
                        strong
                        style={{ fontSize: '13px', display: 'block', marginTop: '4px', color: '#1890ff' }}
                      >
                        Perakende (KDV dahil): {baseCurrencySymbol}{(product.retailPrice * (1 + (product.taxRate ?? 20) / 100)).toFixed(2)}
                      </Text>
                    )}
                    {canSeePrice && product.wholesalePrice && product.wholesalePrice > 0 && (
                      <Text 
                        type="secondary" 
                        style={{ fontSize: '12px', display: 'block', marginTop: '2px' }}
                      >
                        Toptan (KDV dahil): {baseCurrencySymbol}{(product.wholesalePrice * (1 + (product.taxRate ?? 20) / 100)).toFixed(2)}
                      </Text>
                    )}
                    {canSeePrice && product.purchasePrice && product.purchasePrice > 0 && (
                      <Text 
                        type="secondary" 
                        style={{ fontSize: '12px', display: 'block', marginTop: '2px' }}
                      >
                        Alış (KDV dahil): {baseCurrencySymbol}{(product.purchasePrice * (1 + (product.taxRate ?? 20) / 100)).toFixed(2)}
                      </Text>
                    )}
                    {product.totalStock !== undefined && (
                      <Text 
                        type="secondary" 
                        style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}
                      >
                        Stok: {product.totalStock}
                      </Text>
                    )}
                    {product.shelfCode && (
                      <Space style={{ marginTop: '4px', display: 'flex', alignItems: 'center' }}>
                        <Text 
                          type="secondary" 
                          style={{ fontSize: '12px', display: 'block' }}
                        >
                          Raf: {product.shelfCode}
                        </Text>
                        {product.shelfLocations && product.shelfLocations.length > 0 && (
                          <InfoCircleOutlined 
                            style={{ 
                              color: '#1890ff', 
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                            onClick={() => {
                              setSelectedShelfLocations(product.shelfLocations || []);
                              setShelfModalVisible(true);
                            }}
                          />
                        )}
                      </Space>
                    )}
                  </div>
                </Card>
              </Col>
            );
          }
        })}
      </Row>

      {products.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          Ürün bulunamadı
        </div>
      )}

      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Space>
          <Button 
            disabled={pagination.current === 1} 
            onClick={() => handlePageChange(pagination.current - 1)}
          >
            Önceki
          </Button>
          <span>
            Sayfa {pagination.current} / {Math.ceil(pagination.total / pagination.pageSize)}
          </span>
          <Button 
            disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)} 
            onClick={() => handlePageChange(pagination.current + 1)}
          >
            Sonraki
          </Button>
          <Select
            value={pagination.pageSize}
            onChange={(value) => {
              setPagination(prev => ({ ...prev, pageSize: value, current: 1 }));
            }}
            style={{ width: 100 }}
          >
            <Select.Option value={20}>20</Select.Option>
            <Select.Option value={40}>40</Select.Option>
            <Select.Option value={60}>60</Select.Option>
            <Select.Option value={80}>80</Select.Option>
          </Select>
        </Space>
      </div>

      {/* Raf Lokasyonları Popup */}
      <Modal
        title="Raf Lokasyonları"
        open={shelfModalVisible}
        onCancel={() => setShelfModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setShelfModalVisible(false)}>
            Kapat
          </Button>
        ]}
        width={500}
      >
        <List
          dataSource={selectedShelfLocations}
          renderItem={(item) => (
            <List.Item>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <div>
                  <Typography.Text strong>{item.shelfCode}</Typography.Text>
                  <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                    ({item.warehouseName})
                  </Typography.Text>
                </div>
                <Typography.Text type="secondary">
                  Miktar: {item.quantity.toFixed(2)}
                </Typography.Text>
              </Space>
            </List.Item>
          )}
          locale={{ emptyText: 'Raf lokasyonu bulunamadı' }}
        />
      </Modal>
    </div>
  );
};

export default ProductGallery;

